// Shared with client components (no server-only imports), unlike lib/auth.ts.
export const PLATFORM_HOST = 'surveythanks.com'

/**
 * True when this browser can do a WebAuthn ceremony at all — capability only.
 *
 * It says nothing about whether the user HAS a passkey here, and the answer is
 * per-origin: lib/auth.ts builds a per-origin better-auth instance for
 * `/api/auth/passkey/*` (see getPasskeyAuthForOrigin), so each partner custom
 * domain gets its own rpID and credentials do not carry across.
 *
 * `hasPasskeyForThisOrigin` (lib/passkey-hint.ts) narrows that to "and this
 * browser has used one here" — but it is blind to a credential roaming in from
 * iCloud Keychain / Google Password Manager, so gating the sign-in button on it
 * hid the button from people who genuinely had a passkey. Prefer it where the
 * cost of being wrong is noise (an enrolment nudge to someone already enrolled,
 * as in TrustDevicePrompt); on a sign-in affordance, capability alone is the
 * right gate and a wrong guess costs one dismissable NotAllowedError.
 */
export function supportsPasskeys(): boolean {
  return typeof window !== 'undefined' && !!window.PublicKeyCredential
}

/**
 * True when the page is served from the platform host (or a subdomain of it,
 * or localhost). OAuth sign-in (Microsoft / Apple) runs same-origin here: the
 * redirect URIs registered with those providers point at the platform host,
 * and the OAuth state cookie is set on whatever origin starts the flow — so a
 * ceremony started on a partner custom domain could never complete. Partner
 * domains instead relay the ceremony through the platform host and receive
 * the session back via a domain-handoff token (lib/auth/social-relay.ts).
 */
export function isPlatformOrigin(): boolean {
  if (typeof window === 'undefined') return false
  const hostname = window.location.hostname
  return (
    hostname === 'localhost' ||
    hostname === PLATFORM_HOST ||
    hostname.endsWith(`.${PLATFORM_HOST}`)
  )
}
